import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { _isBlank } from '@/littledash';
import type { SpeciesV1 } from '@/model/Species.model';
import type { StrainV1 } from '@/model/Strain.model';

interface UseSpeciesStrainProps {
  selectableSpecies: Array<SpeciesV1>;
  fieldPath?: string;
}

interface UseSpeciesStrainResult {
  speciesFieldName: string;
  strainFieldName: string;
  speciesId: unknown;
  strains: Array<StrainV1>;
  hasSelectableSpecies: boolean;
}

export const useSpeciesStrain = ({
  selectableSpecies,
  fieldPath = '',
}: UseSpeciesStrainProps): UseSpeciesStrainResult => {
  const { control, setValue } = useFormContext();

  const speciesFieldName = _isBlank(fieldPath) ? 'species_id' : `${fieldPath}.species_id`;
  const strainFieldName = _isBlank(fieldPath) ? 'strain_id' : `${fieldPath}.strain_id`;

  const speciesId = useWatch({ control, name: speciesFieldName });

  const speciesIdToStrains = useMemo(
    () => new Map<number, Array<StrainV1>>((selectableSpecies ?? []).map(({ id, strains }) => [id, strains ?? []])),
    [selectableSpecies]
  );

  const strains = useMemo(() => speciesIdToStrains.get(Number(speciesId)) ?? [], [speciesId, speciesIdToStrains]);

  const prevSpeciesIdRef = useRef(speciesId);
  useEffect(() => {
    if (prevSpeciesIdRef.current !== speciesId) {
      setValue(strainFieldName, '');
      prevSpeciesIdRef.current = speciesId;
    }
  }, [speciesId, strainFieldName, setValue]);

  return {
    speciesFieldName,
    strainFieldName,
    speciesId,
    strains,
    hasSelectableSpecies: speciesIdToStrains.size > 0,
  };
};
